import React, { useState, useEffect, useRef, useMemo } from 'react'
import EnhancedLeadsTable, { EnhancedLeadsTableRef } from '@/components/LeadsTable/EnhancedLeadsTable'
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar'
import { Lead } from '@/types/leads/Table'
import { ToastManager } from '@/components/ui/Toast'
import LeadsTour, { isTourCompleted } from '@/components/Tour/LeadsTour'
import TourButton from '@/components/Tour/TourButton'
import { useUsers } from '@/contexts/UsersContext'
import { apiRequest } from '@/utils/api-request'

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

const Leads: React.FC = () => {
  const { activeUsers } = useUsers(); // Use shared context
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [runTour, setRunTour] = useState(false)
  const [showTourBadge, setShowTourBadge] = useState(false)

  // Ref to access EnhancedLeadsTable methods
  const leadsTableRef = useRef<EnhancedLeadsTableRef>(null)

  // Transform active users from context to dropdown format
  const activeUsersForDropdown: UserOption[] = useMemo(() => {
    return activeUsers.map((u) => ({
      value: u.uuid,
      label: u.userName,
      email: u.userEmail,
      role: u.role?.role_name
    }));
  }, [activeUsers]);

  // Show tour badge (tour only starts on-demand via button)
  useEffect(() => {
    const tourCompleted = isTourCompleted()
    if (!tourCompleted) {
      setShowTourBadge(true)
    }
  }, [])

  // Auto-open lead from notification click (?lead_uuid=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const leadUuid = params.get('lead_uuid');
    if (!leadUuid) return;
    apiRequest(`/leads/${leadUuid}`).then((r: any) => {
      if (r.success && r.data) {
        setSelectedLead(r.data);
        setIsLeadModalOpen(true);
      }
    }).catch(() => {});
  }, []);

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead)
    setIsLeadModalOpen(true)
  }

  const closeLeadModal = () => {
    setIsLeadModalOpen(false)
    setSelectedLead(null)
  }

  const handleLeadUpdate = async () => {
    console.log('🔄 Lead updated, refreshing table...')
    await leadsTableRef.current?.refreshLeads()
  }

  const handleStartTour = () => {
    setRunTour(true)
  }

  const handleTourEnd = () => {
    setRunTour(false)
    setShowTourBadge(false)
  }

  return (
    <div className="h-full flex flex-col">
      <ToastManager />
      <div className="flex-1 overflow-auto">
        <EnhancedLeadsTable
          ref={leadsTableRef}
          onLeadClick={handleLeadClick}
          tourButton={<TourButton onClick={handleStartTour} showBadge={showTourBadge} />}
        />
      </div>

      <LeadDetailsSidebar
        isOpen={isLeadModalOpen}
        onClose={closeLeadModal}
        lead={selectedLead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={handleLeadUpdate}
      />

      <LeadsTour runTour={runTour} onTourEnd={handleTourEnd} />
    </div>
  )
}

export default Leads